import { defineStore } from "pinia";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database, Tables } from "~~/types/supabase";

// The organisation switcher is a menu, not a directory. Every login used to
// pull down the account's complete membership list — and persist it to
// localStorage — just to render a handful of rows in the navbar. Accounts that
// belong to a few hundred organisations pay for that on every load. One page at
// a time instead, and the searching happens on the server.
const ORGANIZATION_PAGE_SIZE = 10;
// A search returns its own capped page: enough to pick from, few enough that a
// two-letter query does not quietly undo the paging.
const ORGANIZATION_SEARCH_LIMIT = 20;
// The screens that need a complete picker ask for everything; this is the
// ceiling on "everything".
const ORGANIZATION_MAX = 1000;

type Supabase = SupabaseClient<Database>;

// The membership rights ride along with the organisation row, so resolving the
// active organisation costs no extra request in the common case.
const ORGANIZATION_SELECT = "*, organization_users!inner(rights)";

type OrganizationRow = Tables<"organizations"> & {
  organization_users?: { rights: string[] | null }[] | null;
};

type Membership = {
  organization: Tables<"organizations">;
  rights: string[] | null;
};

const splitRow = (row: OrganizationRow): Membership => {
  const { organization_users, ...organization } = row;
  return {
    organization: organization as Tables<"organizations">,
    rights: organization_users?.[0]?.rights ?? null,
  };
};

// Name then id: a stable order, without which range paging can repeat or skip
// rows whenever two organisations share a name.
const fetchOrganizationPage = async (
  supabase: Supabase,
  userId: string,
  offset: number,
  size = ORGANIZATION_PAGE_SIZE,
) => {
  const { data, error, count } = await supabase
    .from("organizations")
    .select(ORGANIZATION_SELECT, { count: "exact" })
    .eq("organization_users.user_id", userId)
    .order("name")
    .order("id")
    .range(offset, offset + size - 1);

  return {
    error,
    total: count ?? 0,
    rows: ((data ?? []) as unknown as OrganizationRow[]).map(splitRow),
  };
};

// The remembered organisation can sit on any page, so it is looked up by id
// rather than found in whatever page we happen to be holding.
const fetchMembership = async (
  supabase: Supabase,
  userId: string,
  organizationId: string,
): Promise<Membership | null> => {
  const { data, error } = await supabase
    .from("organization_users")
    .select("rights, organization:organizations ( * )")
    .eq("user_id", userId)
    .eq("organization_id", organizationId)
    .maybeSingle();

  if (error || !data?.organization) return null;
  return {
    organization: data.organization as Tables<"organizations">,
    rights: data.rights ?? null,
  };
};

export const useUserStore = defineStore("userStore", {
  state: (): {
    user: Tables<"users"> | null;
    organizations: Tables<"organizations">[] | null;
    organization: Tables<"organizations"> | null;
    organizationsTotal: number;
    organizationsLoadingMore: boolean;
    shop_url: string;
    admin_mode: boolean;
    rights: string[] | null;
    organizationsLoaded: boolean;
    invitations: Database["public"]["Functions"]["my_invitations"]["Returns"];
  } => {
    return {
      user: null,
      // The organisations loaded so far, in name order — not necessarily every
      // organisation the account belongs to. organizationsTotal is the full
      // count on the server.
      organizations: [],
      organization: null,
      organizationsTotal: 0,
      organizationsLoadingMore: false,
      shop_url: useRuntimeConfig().public.shopUrl,
      admin_mode: false,
      rights: null,
      organizationsLoaded: false,
      // Open invitations for your address; the dashboard asks about them (InvitationDialog)
      invitations: [],
    };
  },
  // organizationsLoaded is a per-session signal ("did we fetch the org list
  // from the server yet"), so it must NOT be rehydrated from localStorage —
  // otherwise a stale `true` could trigger the onboarding redirect before the
  // fresh load runs. organizationsLoadingMore is transient for the same
  // reason: a persisted `true` leaves the button spinning forever.
  persist: { omit: ["organizationsLoaded", "organizationsLoadingMore", "invitations"] },
  getters: {
    hasRight: (state) => (right: string): boolean => {
      // admin_mode always grants access (dev only)
      if (state.admin_mode) return true;
      // owner always has full access
      if (state.organization?.owner_id === state.user?.id) return true;
      // null rights = full access (legacy/unrestricted users)
      if (state.rights === null) return true;
      // check specific right in array
      return state.rights.includes(right);
    },
    isOwner: (state): boolean => {
      return state.organization?.owner_id === state.user?.id || state.admin_mode;
    },
    hasMoreOrganizations: (state): boolean =>
      (state.organizations?.length ?? 0) < state.organizationsTotal,
  },
  // could also be defined as
  // state: () => ({ count: 0 })
  actions: {
    async logout() {
      this.user = null;
      this.organization = null;
      const { auth } = useSupabaseClient();
      auth.signOut();
    },
    async loggedIn() {
      const supabase = useSupabaseClient<Database>();
      const user = useSupabaseUser();
      // useSupabaseUser() is populated asynchronously via onAuthStateChange, so
      // right after signInWithPassword resolves it can still be null. Fall back
      // to the stored session (already set at that point) so we never skip the
      // data load and bounce the user to /new-organization.
      let uid = user.value?.sub;
      if (!uid) {
        const { data: { session } } = await supabase.auth.getSession();
        uid = session?.user?.id;
      }
      if (!uid) {
        console.warn("No user logged in");
        return;
      }
      // Fetch user data from Supabase
      const { data, error } = await supabase.from("users").select().eq(
        "id",
        uid,
      ).single();



      if (data?.language) {
        if (data?.language === "en" || data?.language === "nl") {
          useNuxtApp().$i18n.locale.value = data.language;
        }
      }

      this.user = data;
      // Invitations before organizations: organizationsLoaded is what sends someone without an
      // organization to /new-organization, and an invitation should be asked about first.
      await this.loadInvitations();
      await this.loadOrganization();

      if (error) {
        console.error("Error fetching user data:", error);
        return;
      }
    },

    // Deletes a seat map and takes it off the organization's list; the error when the delete fails.
    async deleteSeatingPlan(id: string) {
      const supabase = useSupabaseClient<Database>();
      const { error } = await supabase.from("seating_plans").delete().eq("id", id);
      if (error) return error;
      const org = this.organization;
      if (org?.seating_plan_ids) {
        const updatedIds = org.seating_plan_ids.filter((pid) => pid !== id);
        await supabase.from("organizations").update({ seating_plan_ids: updatedIds }).eq("id", org.id);
        org.seating_plan_ids = updatedIds;
      }
      return null;
    },

    // Also when the account was made outside the join link (register, Google,
    // a confirm mail that lost its redirect).
    async loadInvitations() {
      const { data } = await useSupabaseClient<Database>().rpc("my_invitations");
      this.invitations = data ?? [];
    },
    // Returns false when the invitation is gone, used or for another address.
    async acceptInvitation(id: string) {
      const { data: organizationId } = await useSupabaseClient<Database>()
        .rpc("accept_invitation", { p_invitation: id });
      if (organizationId) {
        // Open the organization you just joined; loadOrganization swaps in the full row by id
        this.organization = { id: organizationId } as Tables<"organizations">;
        await this.loadOrganization();
      }
      // Only now, so the list is never empty while you are still without an organization
      this.invitations = this.invitations.filter((x) => x.id !== id);
      return !!organizationId;
    },
    async declineInvitation(id: string) {
      await useSupabaseClient<Database>().rpc("decline_invitation", { p_invitation: id });
      this.invitations = this.invitations.filter((x) => x.id !== id);
    },

    async loadOrganization() {
      const supabase = useSupabaseClient<Database>();
      const stripe = useStripeStore();
      // Not this.user alone: an account without a public.users row (the signup
      // trigger skips one without full_name) queried user_id=eq. and got a 400,
      // returned before organizationsLoaded, and so never reached
      // /new-organization — leaving "Organization not set" on the event form.
      const userId = this.user?.id ??
        (await supabase.auth.getSession()).data.session?.user.id;
      if (!userId) return;
      const previousOrganizationId = this.organization?.id ?? null;
      // Only the first page of the user's organisations; the count that comes
      // back tells the switcher whether there is more to load.
      const page = await fetchOrganizationPage(supabase, userId, 0);

      if (page.error) {
        console.error("Error fetching organizations:", page.error);
        return;
      }

      this.organizations = page.rows.map((x) => x.organization);
      this.organizationsTotal = page.total;
      this.organizationsLoaded = true;

      if (page.rows.length == 0) {
        return;
      }

      let membership: Membership | null = this.organization
        ? page.rows.find((x) => x.organization.id == this.organization?.id) ??
          null
        : null;
      // Not on the first page: fetch that one membership by id rather than
      // walking every page looking for it.
      if (!membership && this.organization?.id) {
        membership = await fetchMembership(
          supabase,
          userId,
          this.organization.id,
        );
      }
      if (!membership) membership = page.rows[0] ?? null;

      const organization = membership?.organization ?? null;
      this.organization = organization;
      // Only a genuine switch of organization invalidates the Stripe state.
      // This used to reset unconditionally at the top of the method, and
      // loadOrganization is called from ~20 places — including twice inside the
      // onboarding wizard itself. Every one of those wiped the open dialog, the
      // pinned flow variant and the fetched requirements, mid-onboarding.
      if ((organization?.id ?? null) !== previousOrganizationId) stripe.$reset();
      this.rights = membership?.rights ?? null;
      if (this.organization?.slug) {
        if (useRuntimeConfig().public.dev) {
          this.shop_url = "http://" +
            this.organization?.slug + ".localhost:3005";
        } else {
          this.shop_url = "https://" +
            this.organization?.slug + ".tickable.nl";
        }
      } else {
        this.shop_url = useRuntimeConfig().public.shopUrl;
      }

      const eventStore = useEventStore();
      eventStore.loadEvents();
      if (
        this.organization?.payment_method == "stripe" &&
        this.organization?.id != "def086ee-6f4b-4b8b-83f4-31a06f5024d3"
      ) {
        stripe.usingStripe = true;
        stripe.newMethod = false;
      } else {
        stripe.usingStripe = false;
        stripe.newMethod = false;
      }
      await stripe.refreshAccountStatus();
    },

    // The switcher's "show more": one page appended to what is already there.
    async loadMoreOrganizations() {
      if (this.organizationsLoadingMore || !this.hasMoreOrganizations) return;
      const supabase = useSupabaseClient<Database>();
      const loaded = this.organizations?.length ?? 0;
      this.organizationsLoadingMore = true;
      try {
        const page = await fetchOrganizationPage(
          supabase,
          this.user?.id ?? "",
          loaded,
        );
        if (page.error) {
          console.error("Error fetching organizations:", page.error);
          return;
        }
        // A membership added or removed between two requests shifts the window,
        // so the same organisation can come back on two pages.
        const seen = new Set((this.organizations ?? []).map((x) => x.id));
        this.organizations = [
          ...(this.organizations ?? []),
          ...page.rows
            .map((x) => x.organization)
            .filter((x) => !seen.has(x.id)),
        ];
        this.organizationsTotal = page.total;
      } finally {
        this.organizationsLoadingMore = false;
      }
    },

    // For the screens that need a complete picker rather than a switcher (the
    // notification preferences, the OAuth consent screen): they cannot offer a
    // choice out of a list that stops at ten.
    async loadAllOrganizations() {
      if (this.organizationsLoaded && !this.hasMoreOrganizations) return;
      const supabase = useSupabaseClient<Database>();
      this.organizationsLoadingMore = true;
      try {
        const page = await fetchOrganizationPage(
          supabase,
          this.user?.id ?? "",
          0,
          ORGANIZATION_MAX,
        );
        if (page.error) {
          console.error("Error fetching organizations:", page.error);
          return;
        }
        this.organizations = page.rows.map((x) => x.organization);
        this.organizationsTotal = page.total;
        this.organizationsLoaded = true;
      } finally {
        this.organizationsLoadingMore = false;
      }
    },

    // Searching happens in Postgres, not over the page we happen to be holding:
    // otherwise an account with 300 organisations could only ever find the ten
    // it had already downloaded. Results are deliberately not merged into
    // `organizations` — that list stays the paging window.
    async searchOrganizations(
      query: string,
    ): Promise<Tables<"organizations">[]> {
      const term = query.trim();
      if (!term) return [];
      const supabase = useSupabaseClient<Database>();
      const { data, error } = await supabase
        .from("organizations")
        .select(ORGANIZATION_SELECT)
        .eq("organization_users.user_id", this.user?.id ?? "")
        // % and _ are wildcards in LIKE; a typed organisation name is not a
        // pattern.
        .ilike("name", `%${term.replace(/[\\%_]/g, "\\$&")}%`)
        .order("name")
        .order("id")
        .limit(ORGANIZATION_SEARCH_LIMIT);

      if (error) {
        console.error("Error searching organizations:", error);
        return [];
      }
      return ((data ?? []) as unknown as OrganizationRow[])
        .map((x) => splitRow(x).organization);
    },
  },
});

export { ORGANIZATION_PAGE_SIZE, ORGANIZATION_SEARCH_LIMIT };
